import {
  HttpErrorResponse,
  HttpHeaders,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { catchError, of, throwError } from "rxjs";

const STAFF_MICROS = [
  "/ms-hr/",
  "/ms-agreement/",
];

const NO_MOCK = Symbol("NO_CUSTOMER_STAFF_MOCK");
const NOW = "2026-08-20T09:15:00-05:00";
const MOCK_PDF_URL = "/assets/demo/documento-staff.pdf";

/**
 * Keeps the historical Customer workspace usable while the new Staff micros
 * are deployed independently. Healthy responses always win; mock data is only
 * returned after an unavailable Staff endpoint fails.
 */
export const customerStaffMockFallbackInterceptor: HttpInterceptorFn = (
  request,
  next,
) =>
  next(request).pipe(
    catchError((error: unknown) => {
      if (!shouldFallback(request, error)) {
        return throwError(() => error);
      }

      const body = mockBody(request);
      if (body === NO_MOCK) {
        return throwError(() => error);
      }

      return of(
        new HttpResponse({
          status: 200,
          body,
          url: request.urlWithParams,
          headers: new HttpHeaders().set(
            "X-Klab-Data-Source",
            "customer-staff-mock-fallback",
          ),
        }),
      );
    }),
  );

function shouldFallback(
  request: HttpRequest<unknown>,
  error: unknown,
): boolean {
  if (/\/ms-hr\/api\/v1\/(?:hr\/)?vacation(?:[-\/]|$)/.test(request.url)) return false;
  if (!STAFF_MICROS.some((path) => request.url.includes(path))) return false;
  if (!(error instanceof HttpErrorResponse)) return false;
  return error.status === 0 || error.status === 404 || error.status >= 500;
}

function mockBody(request: HttpRequest<unknown>): unknown | typeof NO_MOCK {
  const path = requestPath(request);
  return request.method === "GET"
    ? mockGet(request, path)
    : mockMutation(request, path);
}

function mockGet(
  request: HttpRequest<unknown>,
  path: string,
): unknown | typeof NO_MOCK {
  const employeeMatch = path.match(/\/employees\/(\d+)$/);
  if (employeeMatch) return employee(Number(employeeMatch[1]));
  if (path.endsWith("/employees")) {
    return [employee(numberParam(request, "employeeId") || 1)];
  }

  if (path.endsWith("/contract-types")) return contractTypes();
  if (path.endsWith("/continuity-types")) return continuityTypes();
  if (path.endsWith("/renewal-limits")) return renewalLimits();
  if (path.endsWith("/working-modes")) return workingModes();

  const contractMatch = path.match(/\/contracts\/(\d+)$/);
  if (contractMatch) {
    return contract(
      Number(contractMatch[1]),
      numberParam(request, "employeeId") || 1,
    );
  }
  if (path.endsWith("/contracts")) {
    const employeeId = numberParam(request, "employeeId") || 1;
    return [contract(employeeId, employeeId)];
  }

  if (path.endsWith("/benefit-categories")) return benefitCategories();
  const benefitMatch = path.match(/\/benefits\/(\d+)$/);
  if (benefitMatch)
    return findById(benefits(), "benefitId", Number(benefitMatch[1]));
  if (path.endsWith("/benefits")) return benefits();

  const benefitRequestMatch = path.match(/\/benefit-requests\/(\d+)$/);
  if (benefitRequestMatch) {
    return benefitRequest(Number(benefitRequestMatch[1]), 1, 1);
  }
  if (path.endsWith("/benefit-requests")) {
    return [
      benefitRequest(
        1,
        numberParam(request, "employeeId") || 1,
        numberParam(request, "benefitId") || 1,
      ),
    ];
  }

  if (path.endsWith("/agreement-templates")) return agreementTemplates();
  const signatureListMatch = path.match(/\/agreements\/(\d+)\/signatures$/);
  if (signatureListMatch)
    return agreementSignatures(Number(signatureListMatch[1]));
  const agreementMatch = path.match(/\/agreements\/(\d+)$/);
  if (agreementMatch) {
    const agreementId = Number(agreementMatch[1]);
    return agreement(agreementId, agreementId);
  }
  if (path.endsWith("/agreements")) {
    const contractId = numberParam(request, "sourceReferenceId") || 1;
    return [agreement(contractId, contractId)];
  }

  return NO_MOCK;
}

function mockMutation(
  request: HttpRequest<unknown>,
  path: string,
): unknown | typeof NO_MOCK {
  const payload = recordBody(request.body);

  const agreementSignMatch = path.match(
    /\/agreements\/(\d+)\/parties\/(\d+)\/sign$/,
  );
  if (agreementSignMatch) {
    return {
      signatureId: 2,
      agreementId: Number(agreementSignMatch[1]),
      versionId: 1,
      partyId: Number(agreementSignMatch[2]),
      status: "SIGNED",
      provider: payload["provider"] ?? "KLAB_WEB",
      externalSignatureId: payload["externalSignatureId"] ?? "MOCK-SIGNATURE",
      evidenceUrl: payload["evidenceUrl"] ?? MOCK_PDF_URL,
      signedByUserId: 1001,
      requestedAt: NOW,
      signedAt: NOW,
      updatedAt: NOW,
    };
  }

  if (path.endsWith("/benefit-requests") && request.method === "POST") {
    return {
      ...benefitRequest(
        99,
        Number(payload["employeeId"]) || 1,
        Number(payload["benefitId"]) || 1,
      ),
      ...payload,
      benefitRequestId: 99,
      status: "PENDING",
      createdAt: NOW,
      updatedAt: NOW,
    };
  }

  const benefitCancelMatch = path.match(/\/benefit-requests\/(\d+)$/);
  if (benefitCancelMatch && request.method === "DELETE") {
    return {
      ...benefitRequest(Number(benefitCancelMatch[1]), 1, 1),
      status: "CANCELLED",
      cancelledByUserId: 1001,
      cancelledAt: NOW,
      updatedAt: NOW,
    };
  }

  return NO_MOCK;
}

function employee(employeeId: number) {
  return {
    employeeId,
    identityUserId: 1000 + employeeId,
    companyId: 1,
    branchId: 1,
    departmentId: 2,
    positionId: 4,
    countryId: 173,
    cityId: 1,
    geoDepartmentCode: "15",
    geoProvinceCode: "1501",
    districtCode: "150130",
    leaderEmployeeId: null,
    employeeCode: `EMP-${String(employeeId).padStart(3, "0")}`,
    documentTypeId: 1,
    documentTypeCode: "DNI",
    documentNumber: "81221734",
    firstName: "Rosa",
    lastName: "López",
    secondLastName: "Mori",
    birthDate: "1995-05-17",
    gender: "FEMALE",
    personalEmail: "rosa.lopez@gmail.com",
    corporateEmail: "rosa.lopez@inclub.com",
    phoneCountryCode: "+51",
    phoneNumber: "987654321",
    address: "Lima, Perú",
    timezone: "America/Lima",
    hiredAt: "2025-04-21",
    photoUrl: null,
    status: "ACTIVE",
    createdAt: NOW,
    updatedAt: NOW,
  };
}

function contract(contractId: number, employeeId: number) {
  return {
    contractId,
    employeeId,
    contractTypeId: 1,
    continuityTypeId: 1,
    renewalLimitId: 2,
    workingModeId: 1,
    startDate: "2025-04-21",
    endDate: "2026-10-21",
    currencyCode: "PEN",
    baseSalary: 2000,
    performanceBonus: 150,
    computerAllowance: 100,
    internetAllowance: 100,
    electricityAllowance: 90,
    transportationAllowance: 0,
    contractUrl: MOCK_PDF_URL,
    status: "ACTIVE",
    activatedAt: NOW,
    terminatedAt: null,
    terminationReason: null,
    createdAt: NOW,
    updatedAt: NOW,
  };
}

function contractTypes() {
  return [catalogItem(1, "PAYROLL", "Planilla")];
}

function continuityTypes() {
  return [catalogItem(1, "CONTINUES", "Sí, actualmente tiene este cargo")];
}

function renewalLimits() {
  return [catalogItem(2, "ONE", "1 vez")];
}

function workingModes() {
  return [
    catalogItem(1, "REMOTE", "Remoto"),
    catalogItem(2, "HYBRID", "Híbrido"),
    catalogItem(3, "ONSITE", "Presencial"),
  ];
}

function catalogItem(id: number, code: string, name: string) {
  return { id, code, name, numericValue: null, active: true };
}

function benefitCategories() {
  return [
    {
      categoryId: 1,
      code: "RECREATIONAL",
      name: "Beneficios recreativos",
      description: "Experiencias y recreación",
      sortOrder: 1,
      active: true,
    },
    {
      categoryId: 2,
      code: "TIME_OFF",
      name: "Beneficios de tiempo libre",
      description: "Días y vales de tiempo",
      sortOrder: 2,
      active: true,
    },
  ];
}

function benefits() {
  return [
    benefit(
      1,
      "FULL_DAY",
      "Full day en Club Ribera del Río",
      "RECREATIONAL",
      "/assets/images/benefits/resort-pool.webp",
    ),
    benefit(
      2,
      "BIRTHDAY",
      "Medio día por cumpleaños",
      "TIME_OFF",
      "/assets/images/benefits/birthday-gift.webp",
    ),
    benefit(
      3,
      "VACATION_BONUS",
      "Bono de 15 días extras de vacaciones",
      "TIME_OFF",
      "/assets/images/benefits/family-beach.webp",
    ),
    benefit(
      4,
      "RESTAURANT",
      "20% de descuento en restaurante",
      "RECREATIONAL",
      "/assets/images/benefits/outdoor-restaurant.webp",
    ),
  ];
}

function benefit(
  benefitId: number,
  code: string,
  name: string,
  type: string,
  imageUrl: string,
) {
  return {
    benefitId,
    companyId: 1,
    code,
    name,
    description: `${name}. Disponible para colaboradores activos.`,
    type,
    validFrom: "2026-01-01",
    validUntil: "2026-12-31",
    imageUrl,
    termsUrl: "",
    active: true,
    createdByUserId: 1,
    updatedByUserId: 1,
    createdAt: NOW,
    updatedAt: NOW,
  };
}

function benefitRequest(
  benefitRequestId: number,
  employeeId: number,
  benefitId: number,
) {
  return {
    benefitRequestId,
    benefitId,
    companyId: 1,
    employeeId,
    redeemDate: "2026-09-19",
    message: "Solicito acceder al beneficio.",
    attachmentUrl: "",
    status: "PENDING",
    decisionMessage: "",
    requestedByUserId: 1000 + employeeId,
    decidedByUserId: null,
    decidedAt: null,
    cancelledByUserId: null,
    cancelledAt: null,
    createdAt: NOW,
    updatedAt: NOW,
  };
}

function agreement(agreementId: number, employeeId: number) {
  return {
    agreementId,
    agreementNumber: `AGR-2026-${String(agreementId).padStart(4, "0")}`,
    parentAgreementId: null,
    templateId: 1,
    type: "EMPLOYMENT",
    title: "Contrato de trabajo",
    sourceType: "HR_CONTRACT",
    sourceReferenceId: agreementId,
    startDate: "2025-04-21",
    endDate: "2026-10-21",
    variables: {
      companyName: "Intech World",
      positionName: "Diseñadora UX/UI",
    },
    parties: [
      {
        partyId: 1,
        agreementId,
        role: "EMPLOYER",
        type: "COMPANY",
        referenceId: 1,
        displayName: "Intech World",
        email: "rrhh@inclub.com",
        signingOrder: 1,
        required: true,
        createdAt: NOW,
      },
      {
        partyId: 2,
        agreementId,
        role: "EMPLOYEE",
        type: "EMPLOYEE",
        referenceId: employeeId,
        displayName: "Rosa López Mori",
        email: "rosa.lopez@inclub.com",
        signingOrder: 2,
        required: true,
        createdAt: NOW,
      },
    ],
    status: "PENDING_SIGNATURE",
    currentVersion: 1,
    currentDocumentUrl: MOCK_PDF_URL,
    generatedAt: NOW,
    submittedAt: NOW,
    effectiveAt: null,
    expiredAt: null,
    terminatedAt: null,
    terminationReason: null,
    createdByUserId: 1,
    createdAt: NOW,
    updatedAt: NOW,
  };
}

function agreementSignatures(agreementId: number) {
  return [
    {
      signatureId: 1,
      agreementId,
      versionId: 1,
      partyId: 1,
      status: "SIGNED",
      provider: "KLAB_WEB",
      externalSignatureId: "MOCK-EMPLOYER-SIGNATURE",
      evidenceUrl: MOCK_PDF_URL,
      signedByUserId: 1000,
      requestedAt: NOW,
      signedAt: NOW,
      updatedAt: NOW,
    },
    {
      signatureId: 2,
      agreementId,
      versionId: 1,
      partyId: 2,
      status: "PENDING",
      provider: "KLAB_WEB",
      externalSignatureId: "",
      evidenceUrl: "",
      signedByUserId: null,
      requestedAt: NOW,
      signedAt: null,
      updatedAt: NOW,
    },
  ];
}

function agreementTemplates() {
  return [
    {
      templateId: 1,
      code: "EMPLOYMENT_DEFAULT",
      version: 1,
      type: "EMPLOYMENT",
      name: "Contrato laboral",
      description: "Plantilla de contrato laboral",
      content: "Contrato laboral demostrativo",
      status: "ACTIVE",
      createdByUserId: 1,
      createdAt: NOW,
      updatedAt: NOW,
    },
  ];
}

function requestPath(request: HttpRequest<unknown>): string {
  try {
    return new URL(request.url, "http://klab.local").pathname;
  } catch {
    return request.url.split("?")[0];
  }
}

function stringParam(request: HttpRequest<unknown>, key: string): string {
  const fromParams = request.params.get(key);
  if (fromParams != null) return fromParams;
  try {
    return (
      new URL(request.urlWithParams, "http://klab.local").searchParams.get(
        key,
      ) ?? ""
    );
  } catch {
    return "";
  }
}

function numberParam(request: HttpRequest<unknown>, key: string): number {
  return Number(stringParam(request, key)) || 0;
}

function recordBody(body: unknown): Record<string, unknown> {
  return body && typeof body === "object"
    ? (body as Record<string, unknown>)
    : {};
}

function findById<T extends Record<string, unknown>>(
  items: T[],
  key: keyof T,
  id: number,
): T {
  return items.find((item) => Number(item[key]) === id) ?? items[0];
}
